import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Brain, Database, Target, ListOrdered, Sparkles, CheckCircle2 } from "lucide-react";
import { Heading, Text } from "@/components/Typography";
import { Card } from "@/components/Card";
import { cn } from "@/utils/cn";

interface AIMatchingLoadingPageProps {
  message?: string;
  isCompleting?: boolean;
  onComplete?: () => void;
  estimatedDurationMs?: number;
}

type StepStatus = "done" | "active" | "pending";

const StepRow = ({
  title,
  status,
  icon,
}: {
  title: string;
  status: StepStatus;
  icon: ReactNode;
}) => {
  const StatusIcon = status === "done" ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : icon;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-[12px] border px-3 py-2.5 transition-all",
        status === "active"
          ? "scale-[1.02] border-transparent bg-goal-selected-card shadow-[0_10px_15px_rgba(173,70,255,0.4),0_4px_6px_rgba(173,70,255,0.4)]"
          : status === "pending"
            ? "border-white/5 bg-white/[0.02] opacity-50"
            : "border-secondary-200 bg-secondary-50 dark:border-white/10 dark:bg-white/[0.03]",
      )}
    >
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]",
          status === "active" ? "bg-white/20 text-white" : "bg-goals-purple-selected/20 text-goals-purple-selected",
          status === "pending" && "bg-secondary-200 text-accent-250 dark:bg-white/5 dark:text-pab-dark-subtle-text",
        )}
      >
        {StatusIcon}
      </div>

      <div
        className={cn(
          "font-inter text-[14px] font-medium leading-5 tracking-[-0.3125px]",
          status === "active"
            ? "text-white"
            : status === "pending"
              ? "text-accent-250 dark:text-pab-dark-subtle-text"
              : "text-theme-text-heading",
        )}
      >
        {title}
      </div>
    </div>
  );
};

export const AIMatchingLoadingPage = ({
  message = "Our AI is analyzing your goals and finding the perfect experts for your journey...",
  isCompleting = false,
  onComplete,
  estimatedDurationMs = 9000,
}: AIMatchingLoadingPageProps) => {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const progressRef = useRef(0);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const startAt = performance.now();
    const startProgress = progressRef.current;

    const tick = (now: number) => {
      const elapsed = now - startAt;

      // While loading, ease toward 90–95% (never reaches 100% until we complete).
      if (!isCompleting) {
        const t = Math.min(1, elapsed / Math.max(1, estimatedDurationMs));
        const eased = 1 - Math.pow(1 - t, 3);
        const next = Math.min(95, Math.max(startProgress, eased * 95));
        setProgress(next);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // When loading completes, finish to 100% quickly then signal completion.
      const finishMs = 450;
      const t = Math.min(1, elapsed / finishMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = startProgress + (100 - startProgress) * eased;
      setProgress(next);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (onComplete) {
        window.setTimeout(() => onComplete(), 120);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleting, estimatedDurationMs, onComplete]);

  const stepStatuses = useMemo(() => {
    if (progress >= 100) {
      return ["done", "done", "done", "done", "done"] as const;
    }

    // Map progress → which step is active (matches Figma order).
    const thresholds = [18, 36, 58, 78, 95];
    const activeIdx = thresholds.findIndex((t) => progress < t);
    const idx = activeIdx === -1 ? 4 : activeIdx;
    return [0, 1, 2, 3, 4].map((i) => (i < idx ? "done" : i === idx ? "active" : "pending")) as [
      StepStatus,
      StepStatus,
      StepStatus,
      StepStatus,
      StepStatus,
    ];
  }, [progress]);

  return (
    <div className="bg-surface-page-bg">
      <div className="relative mx-auto flex w-full max-w-[1470px] items-center justify-center px-6 py-6 sm:px-8 sm:py-8">
        {/* Soft blurred blobs (Figma background effect) */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[25%] top-[25%] h-96 w-96 rounded-full bg-primary-purple/10 blur-[64px]" />
          <div className="absolute left-[50%] top-[33%] h-96 w-96 rounded-full bg-primary-650/10 blur-[64px]" />
        </div>

        <Card className="relative w-full max-w-[672px] overflow-y-auto rounded-3xl border border-surface-border bg-surface-card shadow-card-elevated max-sm:scale-[0.94] max-sm:origin-center">
          <div className="px-6 pb-6 pt-6 sm:px-10 sm:pb-8 sm:pt-8">
            {/* Icon */}
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
              <div className="relative h-16 w-16 animate-ai-matching-float">
                <div className="absolute inset-0 rounded-full bg-gradient-to-b from-primary-750 to-primary-purple opacity-50 blur-[32px]" />
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-b from-primary-750 to-primary-purple">
                  <Brain className="h-8 w-8 text-white" aria-hidden="true" />
                </div>
                <div className="absolute -right-1.5 -top-2.5 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-b from-primary-750 to-primary-purple shadow-[0_10px_7.5px_rgba(173,70,255,0.3),0_4px_3px_rgba(173,70,255,0.3)]">
                  <Sparkles className="h-3 w-3 text-white" aria-hidden="true" />
                </div>
              </div>
            </div>

            <Heading
              as="h1"
              className="text-center font-inter text-[22px] font-semibold leading-7 tracking-[0.3955px] text-text-dark"
            >
              AI Matching in Progress
            </Heading>

            <Text className="mt-2 block w-full text-center font-inter text-14 font-normal leading-5 tracking-[-0.4395px] text-ai-goal-back-btn-text">
              {message}
            </Text>

            {/* Progress bar */}
            <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-surface-card-inner">
              <div
                className="h-2 rounded-full bg-progress-gradient transition-[width] duration-150 ease-out"
                style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
              />
            </div>
            <Text className="mt-1.5 block w-full text-center font-inter text-[13px] leading-4 tracking-[-0.1504px] text-theme-text-muted">
              {Math.round(Math.max(0, Math.min(100, progress)))}% Complete
            </Text>

            {/* Steps */}
            <div className="mt-4 flex flex-col gap-2">
              <StepRow
                title="Analyzing your goals and preferences"
                status={stepStatuses[0]}
                icon={<Target className="h-4 w-4" aria-hidden="true" />}
              />
              <StepRow
                title="AI processing your profile data"
                status={stepStatuses[1]}
                icon={<Brain className="h-4 w-4" aria-hidden="true" />}
              />
              <StepRow
                title="Scanning expert database"
                status={stepStatuses[2]}
                icon={<Database className="h-4 w-4" aria-hidden="true" />}
              />
              <StepRow
                title="Finding your perfect matches"
                status={stepStatuses[3]}
                icon={<Sparkles className="h-4 w-4" aria-hidden="true" />}
              />
              <StepRow
                title="Finalizing recommendations"
                status={stepStatuses[4]}
                icon={<ListOrdered className="h-4 w-4" aria-hidden="true" />}
              />
            </div>

            {/* Hint */}
            <div className="mt-4 rounded-[14px] border border-accent-150 bg-accent-350 p-3 dark:border-pab-dark-session-border dark:bg-pab-dark-session-bg">
              <Text className="block w-full text-center font-inter text-[13px] leading-4 tracking-[-0.1504px] text-primary-600 dark:text-pab-dark-session-title">
                💡 Finding your ideal experts - this takes about 20 seconds
              </Text>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AIMatchingLoadingPage;
